import { IdeaIcon } from '@/assets/icons'
import React from 'react'
import BentoTile from './BentoTile'
import { IdeasSection, TileState } from './core'
import EmptyState from './EmptyState'
import { IdeaThumb, ThumbCell, ThumbGrid, ThumbTitle } from './IdeasTile.styles'
import SectionTeaser from './SectionTeaser'
import { iconSizes } from '@/theme/tokens'

interface IdeasTileProps {
	section: IdeasSection
	/** Resolved tile state from the shared state machine (see `resolveTileState`). */
	state: TileState
	/** Navigate to the idea board. */
	onNavigate: () => void
	/** Render a skeleton while the ideas query loads. */
	loading?: boolean
}

/**
 * The ideas bento tile: a single row of up to three idea thumbnails (link
 * previews where available, warm-tint icon-chip fallback otherwise) with
 * truncated titles. Sits under the money tile in the grid's right column.
 * Branches on the resolved tile state like every tile.
 */
const IdeasTile: React.FC<IdeasTileProps> = ({ section, state, onNavigate, loading }) => {
	const icon = <IdeaIcon sx={{ fontSize: iconSizes.sm }} />

	if (state === 'teaser') {
		return <SectionTeaser icon={icon} label="Ideas" count={section.count} colSpan={6} loading={loading} />
	}

	const body =
		state === 'detail' ? (
			<ThumbGrid>
				{section.items.map(idea => (
					<ThumbCell key={idea.id}>
						<IdeaThumb
							url={idea.thumbnailUrl}
							alt=""
							fallbackIcon={<IdeaIcon />}
						/>
						<ThumbTitle>{idea.title}</ThumbTitle>
					</ThumbCell>
				))}
			</ThumbGrid>
		) : (
			<EmptyState
				art="ideas"
				text="Save the places and activities you don't want to miss."
			/>
		)

	return (
		<BentoTile
			icon={icon}
			label="Ideas"
			count={section.count > 0 ? section.count : undefined}
			footer="Browse the idea board"
			ariaLabel="Browse the idea board"
			colSpan={6}
			onClick={onNavigate}
			loading={loading}
		>
			{body}
		</BentoTile>
	)
}

export default IdeasTile
